import { GatewayGetTransactionsExportModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-export.model';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/transaction.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  TRANSACTION_FILTERS_LSK,
  TRANSACTION_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  TRANSACTION_LIST_DEFAULT_OPTIONS,
  TRANSACTION_LIST_FILTER_NAME_LIST,
  TRANSACTION_LIST_SORT_FIELD,
  TRANSACTION_LIST_SORT_ORDER,
  TransactionListOptionsModel,
} from './transaction-list/transaction-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const TransactionContext = createContext<TransactionModel | null>(null);

export const useTransactionContext = (): TransactionModel => {
  const context = useContext(TransactionContext);

  if (!context) {
    throw new Error(
      'useTransactionContext must be used within TransactionContext.Provider',
    );
  }

  return context;
};

interface TransactionContextProviderProps {
  data: TransactionModel;
  children: React.ReactNode;
}

export const TransactionContextProvider = ({
  data,
  children,
}: TransactionContextProviderProps): React.JSX.Element => {
  return (
    <TransactionContext.Provider value={data}>
      {children}
    </TransactionContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface TransactionListContextProps {
  getPaginatedQueryParameters: GatewayGetTransactionsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GatewayGetTransactionsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
  columnOptions: TransactionListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<TransactionListOptionsModel>>;
}

export const TransactionListContext =
  createContext<TransactionListContextProps | null>(null);

export const useTransactionListContext = (): TransactionListContextProps => {
  const context = useContext(TransactionListContext);

  if (!context) {
    throw new Error(
      'useTransactionListContext must be used within TransactionListContext.Provider',
    );
  }

  return context;
};

interface TransactionListContextProviderProps {
  children: React.ReactNode;
}

export const TransactionListContextProvider = ({
  children,
}: TransactionListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GatewayGetTransactionsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        createdAfter: undefined,
        createdBefore: undefined,
        locationId: [],
        chargePointId: [],
        currentState: [],
        textToSearch: '',
        // Sortable fields
        sortField: TRANSACTION_LIST_SORT_FIELD,
        sortOrder: TRANSACTION_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...TRANSACTION_LIST_FILTER_NAME_LIST,
      ],
      TRANSACTION_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<TransactionListOptionsModel>(
      TRANSACTION_LIST_DEFAULT_OPTIONS,
      TRANSACTION_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <TransactionListContext.Provider value={data}>
      {children}
    </TransactionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const ExportTransactionListContext =
  createContext<DeepPartialSkipArrayKey<GatewayGetTransactionsExportModel> | null>(
    null,
  );

export const useExportTransactionListContext =
  (): DeepPartialSkipArrayKey<GatewayGetTransactionsExportModel> => {
    const context = useContext(ExportTransactionListContext);

    if (!context) {
      throw new Error(
        'useExportTransactionListContext must be used within ExportTransactionListContext.Provider',
      );
    }

    return context;
  };

interface ExportTransactionListContextProviderProps {
  data: DeepPartialSkipArrayKey<GatewayGetTransactionsExportModel>;
  children: React.ReactNode;
}

export const ExportTransactionListContextProvider = ({
  data,
  children,
}: ExportTransactionListContextProviderProps): React.JSX.Element => {
  return (
    <ExportTransactionListContext.Provider value={data}>
      {children}
    </ExportTransactionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterTransactionListContext =
  createContext<DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel> | null>(
    null,
  );

export const useFilterTransactionListContext =
  (): DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel> => {
    const context = useContext(FilterTransactionListContext);

    if (!context) {
      throw new Error(
        'useFilterTransactionListContext must be used within FilterTransactionListContext.Provider',
      );
    }

    return context;
  };

interface FilterTransactionListContextProviderProps {
  data: DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterTransactionListContextProvider = ({
  data,
  children,
}: FilterTransactionListContextProviderProps): React.JSX.Element => {
  return (
    <FilterTransactionListContext.Provider value={data}>
      {children}
    </FilterTransactionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchTransactionListContext =
  createContext<DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchTransactionListContext =
  (): DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel> => {
    const context = useContext(FilterSearchTransactionListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchTransactionListContext must be used within FilterSearchTransactionListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchTransactionListContextProviderProps {
  data: DeepPartialSkipArrayKey<GatewayGetTransactionsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchTransactionListContextProvider = ({
  data,
  children,
}: FilterSearchTransactionListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchTransactionListContext.Provider value={data}>
      {children}
    </FilterSearchTransactionListContext.Provider>
  );
};
